import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "control-room-design",
  kind: "horizontal",
  title: "Control Room Design",
  shortTitle: "Control Rooms",
  status: "draft",
  tier: "experience",
  seoTitle: "Control Room Design | General Applications",
  seoDescription:
    "Control room workstations, operator interfaces, video walls, and operational task systems designed around real work and changing conditions.",
  summary:
    "Control-room workstations, operator interfaces, video walls, and task systems designed around the work people perform: seeing changing conditions, coordinating action, and carrying state across shifts without burying the exception.",
  image: "/images/blog/operator-workplaces-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["energy", "media-entertainment", "military"],
  relatedPostSlugs: [
    "operator-workplaces",
    "the-video-wall",
    "no-happy-path",
    "dashboard-design",
    "operational-task-management",
    "interactive-wiring-and-pids",
  ],
  engineering: [
    "Control-room and operator-console interface replacements",
    "Video-wall dashboard software and layout controls",
    "Flexible graphing, indicator groups, and operational planning tools",
    "Multi-monitor operator workspaces and control interfaces",
    "Operational task, schedule, and exception-management systems",
  ],
  phases: {
    study: {
      focus:
        "Model the shift, the room, and the decisions operators must make before selecting displays or furniture.",
      produces: [
        "Operator workflow and handoff model",
        "Room, sightline, and workstation inventory",
        "Source, authority, and exception map",
        "Qualification of the work that belongs in the room",
      ],
      fit: {
        good: [
          "A room has changing operational conditions but no shared, trustworthy view of them",
          "Existing displays, consoles, and work-management tools do not agree on current state",
        ],
        poor: [
          "The need is a presentation wall with no operator workflow or decision-support responsibility",
          "The operating model and source ownership cannot yet be identified",
        ],
      },
    },
    "initial-design": {
      focus:
        "Assign information to the operator workstation, shared displays, and supporting systems by decision and ownership.",
      produces: [
        "Information hierarchy and display-role model",
        "Operator-workstation and video-wall layout concepts",
        "Task-state and exception-transition model",
        "Source integration and control-boundary architecture",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the software, integration, and room-control behavior that keeps the operating picture coherent as conditions change.",
      produces: [
        "Dashboard and control-interface specifications",
        "Video-wall source and layout control contracts",
        "Operational task and handoff workflow definitions",
        "Validation cases for stale data, failed sources, and degraded operation",
      ],
    },
    implementation: {
      focus:
        "Build and validate the operational interfaces with the people who use the room through normal, abnormal, and recovery conditions.",
      produces: [
        "Operator-facing dashboard and workstation software",
        "Video-wall layout and source-control integrations",
        "Task, schedule, and exception-management workflows",
        "Operational validation and transition materials",
      ],
    },
  },
};
